<ng-container *ngIf="currentBound$ | async as currentBound">
  <fin-multi-city-journey-price
    class="ms-medium-y block"
    [fareFamily]="selectedFareFamily$ | async"
    [price]="
      {
        amount: cheapestMatchingOffer$ | finMap: 'price' | async,
        currencyCode: currency$ | async,
      } | finPrice
    "
    (resetSelection)="resetSelection.emit()"
  ></fin-multi-city-journey-price>
  <fin-flight-selection-flight-summary
    [listStyle]="'ordered'"
    [bounds]="selectedBounds$"
    (clickedBound)="resetToBound.emit($event)"
  ></fin-flight-selection-flight-summary>

  <div *ngIf="offersPerBound$ | finMap: '' + (currentBound - 1) | async as currentBoundOffers" class="ms-xxlarge-t">
    <h5 class="font-heading-4" [attr.data-testid]="'bound-title-' + currentBound">
      {{
        'multiCity.flightSelection.boundTitle'
          | finLocalization
            : {
                index: currentBound,
                totalCount: amountOfBounds$ | async,
                origin: flights$ | finMap: currentBound - 1 + '.origin.cityName' | async,
                destination: flights$ | finMap: currentBound - 1 + '.destination.cityName' | async,
              }
      }}
    </h5>
    <span class="font-body-1 nordic-blue-900-text bold-type">{{
      (flights$ | finMap: '' + (currentBound - 1) | async).departureDate | finDateFormat: 'FULL'
    }}</span>
    <ng-container *ngIf="(offersStatus$ | async) === MultiCityStatus.PENDING_MORE_FLIGHTS">
      <fin-loader class="font-body-1" [type]="LoaderType.INLINE" [title]="'ticketSelection.loading'"></fin-loader>
    </ng-container>
    <ng-container *ngIf="(offersStatus$ | async) === MultiCityStatus.READY">
      <div
        *ngFor="let offer of currentBoundOffers; let offerIndex = index; let last = last"
        class="itinerary-card border rounded-large ms-small-t white-bg"
        [class.ms-small-b]="last"
        [class.itinerary-is-selected]="(selectedOffer$ | async)?.itineraryId === offer.itineraryId"
        data-testid="itinerary-card"
        tabindex="0"
        [attr.aria-label]="
          'ticketSelection.aria.boundInformation'
            | finLocalization
              : {
                  departureLocationCode: offer.boundInfo.departure.city,
                  departureDate: offer.boundInfo.departure.dateTime | finDateFormat: 'FULL_NO_YEAR',
                  departureTime: offer.boundInfo.departure.dateTime | finTime,
                  arrivalLocationCode: offer.boundInfo.arrival.city,
                  arrivalDate: offer.boundInfo.arrival.dateTime | finDateFormat: 'FULL_NO_YEAR',
                  arrivalTime: offer.boundInfo.arrival.dateTime | finTime,
                  stops: offer.boundInfo.stops,
                  priceStartingFrom:
                    {
                      amount: cheapestMatchingOffer$ | finMap: 'price' | async,
                      currencyCode: currency$ | async,
                    } | finPrice,
                  operatingAirlineNames: offer.boundInfo.uniqueAirlineNames | finJoin: ', ',
                }
        "
      >
        <ng-container *ngIf="offer.boundInfo as boundInfo">
          <fcom-ufo
            [departure]="{
              dateTime:
                (boundInfo.departure.dateTime | finTime) +
                ' ' +
                (boundInfo.departure.dateTime | finDateFormat: 'WEEKDAY_SHORT'),
              city: boundInfo.departure.city,
              airportCode: boundInfo.departure.airportCode,
              airport: boundInfo.departure.airport,
            }"
            [arrival]="{
              dateTime:
                (boundInfo.arrival.dateTime | finTime) +
                ' ' +
                (boundInfo.arrival.dateTime | finDateFormat: 'WEEKDAY_SHORT'),
              city: boundInfo.arrival.city,
              airportCode: boundInfo.arrival.airportCode,
              airport: boundInfo.arrival.airport,
            }"
            [tails]="boundInfo.tails | finTailNames"
            [duration]="boundInfo.duration | finDuration | async"
            [airlines]="boundInfo.airlines"
            [stops]="boundInfo.stops"
            [isByBusOnly]="boundInfo.isByBusOnly"
            [viewDetailsBtnKey]="'itineraryAndUpgradesModal.viewDetails'"
            (viewDetails)="openViewDetails(offer)"
            [flightNumbers]="boundInfo.bound.itinerary | finFlightNumbers"
            class="block pr-xsmall-t pr-xsmall-x"
          ></fcom-ufo>
          <ng-container *ngIf="hasBusLeg$ | async as hasBusLeg">
            <fcom-notification
              *ngIf="hasBusLeg[offerIndex]"
              class="mr-xsmall-x mr-xsmall-t bound-selection-notes"
              [theme]="NotificationTheme.SUSTAINABILITY"
              [iconName]="SvgLibraryIcon.SUSTAINABILITY"
              [iconCategory]="IconLibrary.SVG_LIBRARY"
              [linkUrl]="'routeNotification.sustainabilityLink.url' | finLocalization"
              [linkText]="'routeNotification.sustainabilityLinkText' | finLocalization"
              [linkOpenInNewWindow]="true"
            >
              <ng-container *ngIf="priorityServicesAvailable$ | async as priorityServicesAvailable">
                <span *ngIf="priorityServicesAvailable[offerIndex]">
                  {{ 'routeNotification.sustainableRoute' | finLocalization }}</span
                >
                <span *ngIf="priorityServicesAvailable[offerIndex] === false">
                  {{ 'routeNotification.sustainableRouteNoPriorities' | finLocalization }}
                </span>
              </ng-container>
            </fcom-notification>
          </ng-container>

          <ng-container *ngIf="hasTrainSegment$ | async as hasTrainSegment">
            @if (hasTrainSegment[offerIndex]) {
              <fcom-notification
                class="mr-xsmall-x mr-xsmall-t bound-selection-notes"
                [theme]="NotificationTheme.INFO"
                [iconCategory]="IconLibrary.SVG_LIBRARY"
                [iconName]="SvgLibraryIcon.TRAIN"
                [linkUrl]="'routeNotification.containsTrainSegment.link.url' | finLocalization"
                [linkText]="'routeNotification.containsTrainSegment.linkText' | finLocalization"
                [linkOpenInNewWindow]="true"
              >
                <span>
                  {{ 'routeNotification.containsTrainSegment.description' | finLocalization }}
                </span>
              </fcom-notification>
            }
          </ng-container>

          <ng-container *ngIf="boundInfo.itineraryWithDifferentCabinClass.length > 0">
            <ng-container
              *ngFor="let itinerary of boundInfo.itineraryWithDifferentCabinClass; trackBy: trackByFunction"
            >
              <ng-container
                *ngIf="
                  [FinnairCabinClass.BUSINESS, FinnairCabinClass.ECOPREMIUM, FinnairCabinClass.ECONOMY].includes(
                    itinerary.cabinClass
                  )
                "
              >
                <fcom-notification
                  data-testid="itinerary-with-different-cabin-class"
                  class="mr-xsmall bound-selection-notes"
                  [theme]="NotificationTheme.WARNING"
                >
                  <ng-container [ngSwitch]="boundInfo.cabinClass">
                    <ng-container *ngSwitchCase="FinnairCabinClass.FIRST">
                      {{
                        'ticketSelection.differentCabinClassFirstWarningNew'
                          | finLocalization
                            : {
                                origin: itinerary.departure.locationCode,
                                destination: itinerary.arrival.locationCode,
                              }
                      }}
                    </ng-container>
                    <ng-container *ngSwitchCase="FinnairCabinClass.BUSINESS">
                      {{
                        'ticketSelection.differentCabinClassWarningNew'
                          | finLocalization
                            : {
                                origin: itinerary.departure.locationCode,
                                destination: itinerary.arrival.locationCode,
                              }
                      }}
                    </ng-container>
                    <ng-container *ngSwitchCase="FinnairCabinClass.ECOPREMIUM">
                      {{
                        'ticketSelection.differentCabinClassPremiumEconomyWarningNew'
                          | finLocalization
                            : {
                                origin: itinerary.departure.locationCode,
                                destination: itinerary.arrival.locationCode,
                              }
                      }}
                    </ng-container>
                    <ng-container *ngSwitchCase="FinnairCabinClass.ECONOMY">
                      {{
                        'ticketSelection.differentCabinClassEconomyWarningNew'
                          | finLocalization
                            : {
                                origin: itinerary.departure.locationCode,
                                destination: itinerary.arrival.locationCode,
                              }
                      }}
                    </ng-container>
                  </ng-container>

                  <fcom-link
                    *ngIf="(selectedOffer$ | async)?.itineraryId === offer.itineraryId"
                    data-testid="multi-city-journey-price-change-ticket-type"
                    class="bold-type block"
                    (afterNavigation)="openViewDetails(offer)"
                  >
                    {{ 'ticketSelection.differentCabinClassWarningLink.linkText' | finLocalization }}
                  </fcom-link>
                </fcom-notification>
              </ng-container>
            </ng-container>
          </ng-container>

          <fcom-notification
            *ngIf="boundInfo.bound.notes?.length > 0"
            class="mr-xsmall bound-selection-notes"
            [theme]="NotificationTheme.WARNING"
          >
            <span *ngFor="let note of boundInfo.bound.notes; let last = last"
              >{{ 'ticketSelection.notes.' + note | finLocalization }}<span *ngIf="!last">, </span></span
            >
          </fcom-notification>
        </ng-container>

        <fin-multi-city-fare-family-selector
          [attr.data-testid]="'multi-city-fare-family-selector-' + offerIndex"
          class="block ms-small-t"
          [basePrice$]="cheapestMatchingOffer$ | finMap: 'price'"
          [fareFamily$]="offer.fareFamily | finToObservable"
          [selectedFareFamily$]="
            ((selectedOffer$ | async)?.itineraryId === offer.itineraryId ? offer.fareFamily : null) | finToObservable
          "
          [currencyCode]="currency$ | async"
          (selectFareFamily)="clickUniqueItinerary(offer)"
        ></fin-multi-city-fare-family-selector>
      </div>
      <fin-multi-city-survey [alwaysRender]="true" class="block mr-small-b"></fin-multi-city-survey>
    </ng-container>
  </div>

  <fcom-modal
    [(open)]="modalOpen"
    (close)="onModalClose()"
    [title]="'itineraryAndUpgradesModal.title' | finLocalization"
    [buttons]="ModalButtons.CLOSE"
  >
    <fcom-tabs
      *ngIf="currentBoundDetails$ | async as boundDetails"
      data-testid="ticket-selection-show-details"
      #tabs
      [layoutType]="TabLayoutType.IN_MODAL"
      (clickedTab)="activeTab$.next($event.index)"
      [prevButtonAriaLabel]="'carousel.previous' | finLocalization"
      [nextButtonAriaLabel]="'carousel.next' | finLocalization"
    >
      <fcom-tab [active]="(activeTab$ | async) === 0" [title]="'itinerary.title' | finLocalization">
        <fin-itinerary-timeline
          [bound]="boundDetails?.boundInfo.bound"
          [boundType]="BoundType.multi"
          [locations]="locations$ | async"
          [title]="'multiCity.flightSelection.flightNumber' | finLocalization: [currentBound]"
          class="block pr-medium"
        >
        </fin-itinerary-timeline>
      </fcom-tab>
      <fcom-tab
        [active]="(activeTab$ | async) === 1"
        [title]="'itineraryAndUpgradesModal.upgradeAvailability.title' | finLocalization"
      >
        <fin-itinerary-upgrade-availability
          *ngIf="upgradeTabSelected$ | async"
          [bound]="boundDetails?.boundInfo.bound"
          [locations]="locations$ | async"
          [selectionPhase$]="selectionPhase$"
          (loginClicked)="triggerLogin()"
        ></fin-itinerary-upgrade-availability>
      </fcom-tab>
    </fcom-tabs>
  </fcom-modal>
</ng-container>

<fcom-cta-button
  data-testid="booking-multi-city-bounds-continue-cta-button"
  [title]="'actions.next' | finLocalization"
  [visible]="!!(selectedOffer$ | async)"
  [position]="ButtonPosition.GRID_FULL"
  (buttonClicked)="continueWithSelection()"
>
  <span class="gray-50-text">{{ 'price.continueButtonTotal' | finLocalization }}: </span>
  <span class="font-body-1 bold-type" data-testid="booking-multi-city-bounds-continue-cta-button-price">{{
    selectedOfferPrice$ | async | finPrice
  }}</span>
</fcom-cta-button>
